import React, { useState, useEffect, useCallback, useRef } from 'react';
import chatApiService from '../services/api';
import { Conversation, Message } from '../utils/types';

interface UseChatResult {
  sendMessage: (message: string, conversationId: number) => void;
  isConnected: boolean;
  isWaiting: boolean;
}

export const useChat = (
  conversations: Conversation[],
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>,
  activeConversation: number | null
): UseChatResult => {
  // Create a ref to track the most recent conversations value
  const conversationsRef = useRef(conversations);

  // Keep the ref up to date with the latest conversations
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const responseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isWaitingRef = useRef<boolean>(false);

  useEffect(() => {
    isWaitingRef.current = isWaiting;
  }, [isWaiting]);

  useEffect(() => {
    // Subscribe to connection status changes
    const unsubscribeConnection = chatApiService.onConnectionChange((connected) => {
      setIsConnected(connected);
    });

    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }

    // Subscribe to incoming messages
    const unsubscribeMessage = chatApiService.onMessage((chatMessage) => {
      if (activeConversation !== null) {
        if (responseTimeoutRef.current) {
          clearTimeout(responseTimeoutRef.current);
          responseTimeoutRef.current = null;
        }

        setIsWaiting(false);

        setConversations((prevConversations) => {
          const updatedConversations = [...prevConversations];
          const currentConversation = updatedConversations[activeConversation];

          if (currentConversation) {
            // Get the last message group ID
            const lastGroupId = currentConversation.messages.length > 0
              ? Math.max(...currentConversation.messages.map(m => m.groupId))
              : 0;

            // Create a new message with a new ID
            const newMessageId = currentConversation.messages.length > 0
              ? Math.max(...currentConversation.messages.map(m => m.id)) + 1
              : 1;

            const newMessage: Message = {
              id: newMessageId,
              groupId: lastGroupId,
              type: 'answer',
              content: chatMessage.content,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              expanded: true,
              tableData: chatMessage.tableData
            };

            currentConversation.messages.push(newMessage);
          }

          return updatedConversations;
        });
      }
    });

    // Cleanup subscriptions when component unmounts
    return () => {
      unsubscribeConnection();
      unsubscribeMessage();

      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
    };
  }, [activeConversation, setConversations]);

  const sendMessage = useCallback((message: string, conversationId: number) => {
    console.log(`useChat.sendMessage called with message: "${message}" for conversation ID: ${conversationId}`);

    // Always use the latest conversations from the ref
    const latestConversations = conversationsRef.current;

    // First, find the conversation by ID
    const conversationIndex = latestConversations.findIndex(conv => conv.id === conversationId);
    console.log(`Found conversation at index: ${conversationIndex} out of ${latestConversations.length} conversations`);

    if (conversationIndex !== -1) {
      // Add the user message to the conversation
      const currentConversation = latestConversations[conversationIndex];
      console.log(`Current conversation: ID=${currentConversation.id}, Title=${currentConversation.title}, Messages=${currentConversation.messages.length}`);

      const newQuestionGroupId = currentConversation.messages.length > 0
        ? Math.max(...currentConversation.messages.map(m => m.groupId || 0)) + 1
        : 1;

      const newMessageId = currentConversation.messages.length > 0
        ? Math.max(...currentConversation.messages.map(m => m.id)) + 1
        : 1;

      console.log(`Created new message with ID=${newMessageId} in group ${newQuestionGroupId}`);

      const questionMessage: Message = {
        id: newMessageId,
        groupId: newQuestionGroupId,
        type: 'question',
        content: message,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      // Update conversations with user message
      setConversations(prevConversations => {
        const updatedConversations = [...prevConversations];
        // Double check the conversation index is still valid
        if (conversationIndex < updatedConversations.length) {
          updatedConversations[conversationIndex].messages.push(questionMessage);
          console.log(`Added message to conversation. Now has ${updatedConversations[conversationIndex].messages.length} messages`);
        } else {
          console.error("Conversation index no longer valid!");
        }
        return updatedConversations;
      });

      // Set waiting state to show loading indicator
      setIsWaiting(true);

      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }

      responseTimeoutRef.current = setTimeout(() => {
        if (isWaitingRef.current) {
          setIsWaiting(false);

          setConversations(prevConversations => {
            const updatedConversations = [...prevConversations];
            const convIndex = updatedConversations.findIndex(conv => conv.id === conversationId);

            if (convIndex !== -1) {
              const conversation = updatedConversations[convIndex];
              const lastGroupId = conversation.messages.length > 0
                ? Math.max(...conversation.messages.map(m => m.groupId))
                : 0;

              const newMessageId = conversation.messages.length > 0
                ? Math.max(...conversation.messages.map(m => m.id)) + 1
                : 1;

              const fallbackMessage: Message = {
                id: newMessageId,
                groupId: lastGroupId,
                type: 'answer',
                content: "Alex couldn't find the answer to your question.",
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                expanded: true
              };

              conversation.messages.push(fallbackMessage);
            }

            return updatedConversations;
          });
        }

        responseTimeoutRef.current = null;
      }, 60000);

      // Send the message to the API immediately
      console.log(`Sending message to API for conversation ID: ${currentConversation.id}`);
      chatApiService.sendMessage(message, currentConversation.id);
    } else {
      console.error(`Conversation with ID ${conversationId} not found in conversations array!`);
      console.log('Available conversations:', latestConversations.map(c => `ID: ${c.id}, Title: ${c.title}`));

      // Try one more time with a delay in case the state hasn't updated yet
      setTimeout(() => {
        const retryConversations = conversationsRef.current;
        const retryIndex = retryConversations.findIndex(conv => conv.id === conversationId);

        if (retryIndex !== -1) {
          console.log(`Retry found conversation at index: ${retryIndex}`);
          const retryConversation = retryConversations[retryIndex];
          chatApiService.sendMessage(message, retryConversation.id);
        } else {
          console.error(`Retry still couldn't find conversation ${conversationId}`);

          // Last resort - just send the message directly
          console.log("Sending message directly as last resort");
          chatApiService.sendMessage(message, conversationId);
        }
      }, 500);
    }
  }, [setConversations]); // No longer dependent on the conversations array

  return {
    sendMessage,
    isConnected,
    isWaiting
  };
};